import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Paper, Grid, Button, Chip, CircularProgress } from '@mui/material';
import {
    Person as PersonIcon,
    Business as BusinessIcon,
    AccountBalance as AccountBalanceIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    Article as ArticleIcon,
    RestaurantMenu as RestaurantIcon,
    Hotel as HotelIcon,
    ReceiptLong as ReceiptLongIcon,
    AttachMoney as AttachMoneyIcon,
    CalendarMonth as CalendarIcon,
    AccessTime as AccessTimeIcon,
    Check,
    Clear
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { theme } from '../../ConstantColors';
import { RejectionDialog } from './rejectionReasonDialog';
import MainCard from 'ui-component/cards/MainCard';
import { useAcquittals } from 'hooks/api/travel-acquital/useTravelAcquital';
import AcquittalLineActivitiesTable from './acquitalLineActivities';
import { useKeycloak } from '@react-keycloak/web';
import { BASE_URL } from 'utils/axios';

// Reusable components
const SectionTitle = ({ icon: Icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Icon sx={{ color: theme.primary.main }} />
        <Typography variant="h6" sx={{ color: theme.primary.main, fontWeight: 600 }}>
            {title}
        </Typography>
    </Box>
);

const InfoSection = ({ title, icon: Icon, children, elevation = 1, sx = {} }) => (
    <Paper elevation={elevation} sx={{ p: 4, mb: 4, borderRadius: 2, ...sx }}>
        <SectionTitle icon={Icon} title={title} />
        {children}
    </Paper>
);

const LabelValue = ({ label, value, icon: Icon, chip = false }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {Icon && <Icon fontSize="small" />}
                {label}
            </Box>
        </Typography>
        {chip ? (
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: theme.primary.light,
                    color: theme.primary.main,
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500
                }}
            >
                {value || '-'}
            </Box>
        ) : (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {value || '-'}
            </Typography>
        )}
    </Box>
);

const AcquittalPreview = () => {
    const { acquittalId } = useParams();
    const navigate = useNavigate();
    const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { getAcquittalById, selectedAcquittal: data, approveAcquittals } = useAcquittals();
    const [lineActivities, setLineActivities] = useState([]);
    const { keycloak } = useKeycloak();
    const [isLoading, setIsLoading] = useState(false);

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || Number.isNaN(amount)) {
            return '$0.00';
        }
        return `$${Number(amount).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFormattedData = (rawData) => {
        if (!rawData) return null;

        return {
            totalSpentAmount: 0,
            clientReceivedAmount: 0,
            varianceAmount: 0,
            breakfastVariance: 0,
            lunchVariance: 0,
            dinnerVariance: 0,
            incidentalVariance: 0,
            accommodationVariance: 0,
            provedAccommodationVariance: 0,
            unprovedAccommodationVariance: 0,
            branchCode: {
                code: '',
                name: '',
                description: ''
            },
            department: {
                projectName: '',
                groupCode: '',
                groupCodeDescription: ''
            },
            user: {
                firstName: '',
                lastName: '',
                email: '',
                employeeNumber: '',
                employeeTitle: '',
                status: ''
            },
            ...rawData
        };
    };

    const formattedData = getFormattedData(data);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await getAcquittalById(acquittalId);
            } catch (err) {
                console.error('Error fetching acquittal details:', err);
                setError('Failed to load acquittal details');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [acquittalId, getAcquittalById]);

    // Fetch line activities
    useEffect(() => {
        const fetchLineActivities = async () => {
            if (!acquittalId || !keycloak?.token) return;

            try {
                setIsLoading(true);
                const response = await fetch(`${BASE_URL}/api/acquittals/${acquittalId}/acquittal-line-activities`, {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${keycloak.token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Request failed: ${response.status}`);
                }

                const activities = await response.json();
                setLineActivities(activities);
            } catch (error) {
                console.error('Error fetching line activities:', error);
                setLineActivities([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLineActivities();
    }, [acquittalId, keycloak?.token]);

    const handleApprove = () => {
        Swal.fire({
            title: 'Approve Acquittal',
            html: `
                <div>
                    <p>Are you sure you want to approve this acquittal?</p>
                    <textarea id="comment" class="swal2-textarea" placeholder="Add optional comment (not required)"></textarea>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: theme.primary.main,
            cancelButtonColor: theme.error.main,
            confirmButtonText: 'Yes, Approve',
            cancelButtonText: 'Cancel',
            preConfirm: () => document.getElementById('comment').value
        }).then(async (result) => {
            if (result.isConfirmed && formattedData?.id) {
                const currentRole = formattedData.currentApprovalStage;
                const comment = result.value || ''; // Empty string if no comment provided

                try {
                    const validStages = ['ROLE_SUPERVISOR', 'ROLE_FINANCE', 'ROLE_HOD'];
                    const { currentApprovalStage } = formattedData;

                    if (!validStages.includes(currentApprovalStage)) {
                        throw new Error('Invalid approval stage');
                    }

                    await approveAcquittals([formattedData.id], currentApprovalStage, 'APPROVED', comment);

                    await Swal.fire({
                        title: 'Approved!',
                        text: 'The acquittal has been approved successfully.',
                        icon: 'success',
                        confirmButtonColor: theme.primary.main
                    });

                    if (currentRole === 'ROLE_SUPERVISOR') {
                        navigate('/dashboard/trips-adminstration-s');
                    } else if (currentRole === 'ROLE_FINANCE') {
                        navigate('/dashboard/trips-adminstration-f');
                    } else if (currentRole === 'ROLE_HOD') {
                        navigate('/dashboard/trips-adminstration-h');
                    }
                } catch (error) {
                    console.error('Error approving acquittal:', error);
                    Swal.fire({
                        title: 'Error!',
                        text: `Failed to approve acquittal: ${error.response?.data?.message || error.message}`,
                        icon: 'error',
                        confirmButtonColor: theme.primary.main
                    });
                }
            }
        });
    };

    // const handleApprove = () => {
    //     Swal.fire({
    //         title: 'Confirm Approval',
    //         text: 'Are you sure you want to approve this acquittal?',
    //         icon: 'question',
    //         showCancelButton: true,
    //         confirmButtonColor: theme.primary.main,
    //         cancelButtonColor: theme.error.main,
    //         confirmButtonText: 'Yes, Approve',
    //         cancelButtonText: 'Cancel'
    //     }).then(async (result) => {
    //         if (result.isConfirmed && formattedData?.id) {
    //             const currentRole = formattedData.currentApprovalStage;
    //             try {
    //                 const validStages = ['ROLE_SUPERVISOR', 'ROLE_FINANCE', 'ROLE_HOD'];
    //                 const { currentApprovalStage } = formattedData;

    //                 if (!validStages.includes(currentApprovalStage)) {
    //                     throw new Error('Invalid approval stage');
    //                 }

    //                 await approveAcquittals([formattedData.id], currentApprovalStage, 'APPROVED', '');

    //                 await Swal.fire({
    //                     title: 'Approved!',
    //                     text: 'The acquittal has been approved successfully.',
    //                     icon: 'success',
    //                     confirmButtonColor: theme.primary.main
    //                 });

    //                 if (currentRole === 'ROLE_SUPERVISOR') {
    //                     navigate('/dashboard/trips-adminstration-s');
    //                 } else if (currentRole === 'ROLE_FINANCE') {
    //                     navigate('/dashboard/trips-adminstration-f');
    //                 } else if (currentRole === 'ROLE_HOD') {
    //                     navigate('/dashboard/trips-adminstration-h');
    //                 }
    //             } catch (error) {
    //                 console.error('Error approving acquittal:', error);
    //                 Swal.fire({
    //                     title: 'Error!',
    //                     text: `Failed to approve acquittal: ${error.response?.data?.message || error.message}`,
    //                     icon: 'error',
    //                     confirmButtonColor: theme.primary.main
    //                 });
    //             }
    //         }
    //     });
    // };

    const handleReject = async (reasonText) => {
        if (typeof reasonText === 'string' && reasonText.trim() && formattedData?.id) {
            try {
                const currentRole = formattedData.currentApprovalStage;
                const validStages = ['ROLE_SUPERVISOR', 'ROLE_FINANCE', 'ROLE_HOD'];
                const { currentApprovalStage } = formattedData;

                if (!validStages.includes(currentApprovalStage)) {
                    throw new Error('Invalid approval stage');
                }

                await approveAcquittals([formattedData.id], currentApprovalStage, 'DECLINED', reasonText);

                await Swal.fire({
                    title: 'Rejected!',
                    text: 'The acquittal has been rejected successfully.',
                    icon: 'success',
                    confirmButtonColor: theme.primary.main
                });

                if (currentRole === 'ROLE_SUPERVISOR') {
                    navigate('/dashboard/trips-adminstration-s');
                } else if (currentRole === 'ROLE_FINANCE') {
                    navigate('/dashboard/trips-adminstration-f');
                } else if (currentRole === 'ROLE_HOD') {
                    navigate('/dashboard/trips-adminstration-h');
                }
            } catch (error) {
                console.error('Error rejecting acquittal:', error);
                Swal.fire({
                    title: 'Error!',
                    text: `Failed to reject acquittal: ${error.response?.data?.message || error.message}`,
                    icon: 'error',
                    confirmButtonColor: theme.primary.main
                });
            }
            setIsRejectionDialogOpen(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!formattedData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography>Acquittal not found</Typography>
            </Box>
        );
    }

    return (
        <MainCard>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>
                        Acquittal Details - A{formattedData.id}
                    </Typography>
                </Box>

                {/* Status Banner */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 4,
                        bgcolor: theme.primary.main,
                        color: 'white',
                        borderRadius: 2
                    }}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="h5">Status</Typography>
                            <Chip
                                label={formattedData.status || 'N/A'}
                                sx={{
                                    mt: 1,
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white'
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="h5">Created</Typography>
                            <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                                {formatDate(formattedData.createdAt)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="h5">Current Stage</Typography>
                            <Chip
                                label={formattedData.currentApprovalStage?.replace('ROLE_', '') || 'N/A'}
                                sx={{
                                    mt: 1,
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white'
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="h5">Last Modified</Typography>
                            <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                                {formatDate(formattedData.lastModifiedAt)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Basic Information */}
                <InfoSection title="Basic Information" icon={ArticleIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Branch Code" value={formattedData.branchCode?.code} chip />
                            <LabelValue label="Branch Name" value={formattedData.branchCode?.name} icon={BusinessIcon} />
                            <LabelValue label="Branch Description" value={formattedData.branchCode?.description} icon={BusinessIcon} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Department" value={formattedData.department?.projectName} chip />
                            <LabelValue label="Group Code" value={formattedData.department?.groupCode} icon={BusinessIcon} />
                            <LabelValue label="Group" value={formattedData.department?.groupCodeDescription} icon={BusinessIcon} />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* User Information */}
                <InfoSection title="User Information" icon={PersonIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue
                                label="Full Name"
                                value={`${formattedData.user?.firstName || ''} ${formattedData.user?.lastName || ''}`}
                                icon={PersonIcon}
                            />
                            <LabelValue label="Email" value={formattedData.user?.email} icon={EmailIcon} />
                            <LabelValue label="Employee Title" value={formattedData.user?.employeeTitle} icon={BadgeIcon} />
                            <LabelValue label="Employee Number" value={formattedData.user?.employeeNumber} icon={BadgeIcon} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Status" value={formattedData.user?.status} chip />
                            <LabelValue label="Date Received" value={formatDate(formattedData.dateReceived)} icon={CalendarIcon} />
                            <LabelValue
                                label="Last Status Change"
                                value={formatDate(formattedData.lastStatusChange)}
                                icon={AccessTimeIcon}
                            />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Financial Details */}
                <InfoSection title="Financial Details" icon={AccountBalanceIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <LabelValue
                                label="Total Spent Amount"
                                value={formatCurrency(formattedData.totalSpentAmount)}
                                icon={AttachMoneyIcon}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <LabelValue
                                label="Received Amount"
                                value={formatCurrency(formattedData.clientReceivedAmount)}
                                icon={AttachMoneyIcon}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <LabelValue
                                label="Variance Amount"
                                value={formatCurrency(formattedData.varianceAmount)}
                                chip
                                icon={AttachMoneyIcon}
                            />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Variance Breakdown */}
                <InfoSection title="Variance Breakdown" icon={ReceiptLongIcon}>
                    <Box sx={{ mt: 4 }}>
                        <AcquittalLineActivitiesTable
                            lineActivities={lineActivities}
                            formatCurrency={formatCurrency}
                            acquittalData={formattedData}
                        />
                    </Box>
                </InfoSection>

                {/* Notes Section */}
                <InfoSection title="Additional Notes" icon={ArticleIcon}>
                    <Typography variant="body1">{formattedData.notes || 'No additional notes provided.'}</Typography>
                </InfoSection>

                {/* Action Buttons */}
                {formattedData.status === 'PENDING' && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 4,
                            p: 3,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            borderRadius: 2,
                            bgcolor: theme.background.light,
                            borderTop: `1px solid ${theme.primary.light}`
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => setIsRejectionDialogOpen(true)}
                            sx={{
                                bgcolor: theme.error.main,
                                '&:hover': { bgcolor: theme.error.dark }
                            }}
                            startIcon={<Clear />}
                        >
                            Reject Acquittal
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApprove}
                            sx={{
                                bgcolor: theme.primary.main,
                                '&:hover': { bgcolor: theme.primary.dark }
                            }}
                            startIcon={<Check />}
                        >
                            Approve Acquittal
                        </Button>
                    </Paper>
                )}
            </Box>

            {/* Rejection Dialog */}
            <RejectionDialog
                open={isRejectionDialogOpen}
                onClose={() => setIsRejectionDialogOpen(false)}
                onReject={handleReject}
                requestNumber={formattedData?.id}
            />
        </MainCard>
    );
};

// PropTypes definitions
SectionTitle.propTypes = {
    icon: PropTypes.elementType.isRequired,
    title: PropTypes.string.isRequired
};

InfoSection.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    children: PropTypes.node.isRequired,
    elevation: PropTypes.number,
    sx: PropTypes.object
};

LabelValue.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    icon: PropTypes.elementType,
    chip: PropTypes.bool
};

// Default props
InfoSection.defaultProps = {
    elevation: 1,
    sx: {}
};

LabelValue.defaultProps = {
    icon: null,
    chip: false,
    value: '-'
};

export default AcquittalPreview;
